import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { Partner } from "./@types";
import {
  useAddPartnerMutation,
  useUpdatePartnerMutation,
} from "./partnerApiSlice";

export const userFormSchema = z.object({
  name: z.string().nonempty("Name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().nonempty("Phone number is required"),
  address: z.string().nonempty("Address is required"),
});

export type UserFormData = z.infer<typeof userFormSchema>;

interface PartnerModelProps {
  isOpen: boolean;
  onClose: () => void;
  partner?: Partner | null;
  mode?: "add" | "edit";
}

const PartnerModal: React.FC<PartnerModelProps> = ({
  isOpen,
  onClose,
  partner = null,
  mode = partner ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && partner;

  const [
    createUser,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useAddPartnerMutation();

  const [
    updateUser,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdatePartnerMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(userFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      address: "",
    },
  });

  // Populate form with partner data when editing
  useEffect(() => {
    if (isEditMode && partner) {
      reset({
        name: partner.fullName || "",
        email: partner.email || "",
        phoneNumber: partner.phoneNumber || "",
        address: partner.address || "",
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        name: "",
        email: "",
        phoneNumber: "",
        address: "",
      });
    }
  }, [isEditMode, partner, reset]);

  const onSubmit = async (data: UserFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        fullName: data.name,
        email: data.email,
        phoneNumber: data.phoneNumber,
        address: data.address,
      };

      if (isEditMode && partner?.id) {
        await updateUser({
          id: partner.id,
          ...payload,
        }).unwrap();
      } else {
        await createUser(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} partner. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Partner ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} partner. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Partner has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess
    ? ""
    : `${isEditMode ? "Edit" : "Add"} partner`;
  const submitButtonText = isEditMode ? "Update partner" : "+ Add partner";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <AppInputField
                  label="Enter name"
                  type="text"
                  error={errors.name?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter name"
                />
              )}
            />

            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <AppInputField
                  label="Enter email"
                  type="email"
                  error={errors.email?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter email"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <AppInputField
                  label="Phone number"
                  type="tel"
                  error={errors.phoneNumber?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter phone number"
                />
              )}
            />

            <Controller
              control={control}
              name="address"
              render={({ field }) => (
                <AppInputField
                  label="Address"
                  type="text"
                  error={errors.address?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter address"
                />
              )}
            />
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PartnerModal;
